import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Departures, SalesTypes } from 'app/core/enums/common.enum';
import { TotalsLocal } from 'app/core/interfaces/finances';
import { FinanceResources } from 'app/core/security/resources/resources';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { WindowsSalesService } from '../../services/sales/midway/windows-sales.service';
import { TotalGrossService } from '../../services/sales/total-gross.service';
import * as WindowSalesActions from './window-sales.actions';

@Injectable()
export class WindowSalesEffects {
    private _actions$ = inject(Actions);
    private _windowSalesService = inject(WindowsSalesService);
    private _alertService = inject(AlertService);
    private _adapter = inject(AdapterService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _totalGrossService = inject(TotalGrossService);

    messages = {
        getAllError: 'Error getting all window sales',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All WindowSales
    getAllWindowSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(WindowSalesActions.loadWindowsSales),
            switchMap((action) => {
                return this._windowSalesService.getAll(action.playdateId).pipe(
                    map((res) => {
                        const data = this._adapter.adaptWindowsSalesList(
                            res.data
                        );
                        const totalMoney = this._adapter.adaptWindowsTotalMoney(
                            res.total_money
                        );

                        return WindowSalesActions.loadWindowsSalesSuccess({
                            data,
                            totalMoney,
                        });
                    }),
                    catchError((error) =>
                        of(
                            WindowSalesActions.loadWindowsSalesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create WindowSale
    createWindowSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(WindowSalesActions.createWindowsSale),
            switchMap((action) => {
                return this._windowSalesService.create(action.data).pipe(
                    map((res) => {
                        const sale = this._adapter.adaptWindowsSale(res.data);

                        return WindowSalesActions.createWindowsSaleSuccess({
                            sale,
                            success: 'Window sale created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            WindowSalesActions.createWindowsSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update WindowSale
    updateWindowSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(WindowSalesActions.updateWindowsSale),
            switchMap((action) => {
                return this._windowSalesService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            const data = this._adapter.adaptWindowsSale(
                                res.data
                            );
                            return WindowSalesActions.updateWindowsSaleSuccess({
                                data,
                                success: 'Window sale updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                WindowSalesActions.updateWindowsSaleFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Sale (all items in a location group)
    deleteWindowSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(WindowSalesActions.deleteWindowsSale),
            switchMap((action) => {
                return this._windowSalesService.delete(action.payload).pipe(
                    map((res) => {
                        const data = res.data;
                        return WindowSalesActions.deleteWindowsSaleSuccess({
                            deletedSales: data.deleted_sales,
                            remainingSales: data.remaining_sales,
                            success: 'Window sale deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            WindowSalesActions.deleteWindowsSaleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Sale Item (single child)
    deleteWindowSaleItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(WindowSalesActions.deleteWindowsSaleItem),
            switchMap((action) => {
                return this._windowSalesService.delete(action.payload).pipe(
                    map((res) => {
                        // console.log(res);

                        const saleId = Number(res.data.deleted_sales[0]);
                        const totals = res.data.totals;
                        const totalsLocal: TotalsLocal = {
                            ...res.data.total_local,
                            amount: res.data.total_local.gross,
                        };

                        if (!saleId) {
                            const message =
                                'Sale item ID not found in response';
                            this._alertService.showAlert(message, 'error');
                            throw new Error(message);
                        }

                        return WindowSalesActions.deleteWindowsSaleItemSuccess({
                            deletedSaleItem: saleId,
                            totals,
                            totalsLocal,
                            success: 'Window sale item deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            WindowSalesActions.deleteWindowsSaleItemFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onWindowSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    WindowSalesActions.createWindowsSaleSuccess,
                    WindowSalesActions.updateWindowsSaleSuccess,
                    WindowSalesActions.deleteWindowsSaleSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    this._venueFinancesService.onUpdateFinances = true;
                    this._totalGrossService.onValidateFinances(
                        Departures.MIDWAY,
                        SalesTypes.SALE_MIDWAY,
                        FinanceResources.WINDOWS_SALES
                    );
                })
            ),
        { dispatch: false }
    );

    onWindowSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    WindowSalesActions.loadWindowsSalesFailure,
                    WindowSalesActions.createWindowsSaleFailure,
                    WindowSalesActions.updateWindowsSaleFailure,
                    WindowSalesActions.deleteWindowsSaleFailure,
                    WindowSalesActions.deleteWindowsSaleItemFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
